import { z } from "zod";

import type {
  ChargeUnit,
  MembershipScope,
  PricingService,
  RepeatType,
} from "@/libs/ratesApi";

// Shared rate-form contract for the Rates workspace + grouped rule editor.
//
// The enum const arrays are defined HERE (not imported as VALUES from ratesApi)
// because the component tests mock @/libs/ratesApi with only the CRUD fns — a
// value-import of PRICING_SERVICES et al. would be `undefined` under the mock
// and crash z.enum() at module load. ratesApi.ts owns the AUTHORITATIVE union
// TYPES (imported above, type-only — erased at runtime); these arrays are typed
// against those unions, so a backend value change still flows through the types.

export const PRICING_SERVICES: PricingService[] = [
  "court_reservation",
  "open_play",
  "classes",
  "coaching",
  "memberships",
];
export const CHARGE_UNITS: ChargeUnit[] = ["per_hour", "per_session", "flat"];
export const REPEAT_TYPES: RepeatType[] = [
  "never",
  "daily",
  "weekdays",
  "weekends",
  "weekly",
];
export const MEMBERSHIP_SCOPES: MembershipScope[] = [
  "any",
  "non_member",
  "any_member",
  "specific_plans",
];

// Human labels for the enum tokens. The wire carries the token; the UI shows
// the label. A label change lands in one place.
export const SERVICE_LABELS: Record<PricingService, string> = {
  court_reservation: "Court reservation",
  open_play: "Drop-in",
  classes: "Classes",
  coaching: "Coaching",
  memberships: "Memberships",
};
export const CHARGE_UNIT_LABELS: Record<ChargeUnit, string> = {
  per_hour: "Per hour",
  per_session: "Per session",
  flat: "Flat",
};
export const MEMBERSHIP_SCOPE_LABELS: Record<MembershipScope, string> = {
  any: "Any",
  non_member: "Non-member",
  any_member: "Any member",
  specific_plans: "Specific plans",
};
// The TRIGGER (selected-value) display for the Member-scope select. It mirrors
// MEMBERSHIP_SCOPE_LABELS except for "specific_plans", whose trigger reads
// "Specific" (no "plans"). Radix renders a hidden native <option> mirror whose
// text is the dropdown item's label ("Specific plans") and that cannot be
// overridden; keeping the word "plans" off the TRIGGER means a getByText(/plan/i)
// matches only that single hidden mirror, never the trigger too — so the
// validation message remains the unambiguous error in view. The dropdown ITEM
// keeps the full "Specific plans" wording so it is pickable by its role name.
export const MEMBERSHIP_SCOPE_TRIGGER_LABELS: Record<MembershipScope, string> =
  {
    ...MEMBERSHIP_SCOPE_LABELS,
    specific_plans: "Specific",
  };
// "Mon–Fri" rather than "Weekdays" so the option text (and its hidden native
// <option> mirror that Radix Select renders for form autofill) never contains
// the substring "day" — which would double-match a getByText(/day/i) probe of
// the weekly-days validation message.
export const REPEAT_LABELS: Record<RepeatType, string> = {
  never: "Never",
  daily: "Daily",
  weekdays: "Mon–Fri",
  weekends: "Weekends",
  weekly: "Weekly",
};

// days_of_week wire convention is 0=Mon .. 6=Sun (NOT JS getDay). The form
// presents day names; the integer index is what goes on the wire.
export const DAYS: { value: number; label: string; long: string }[] = [
  { value: 0, label: "Mon", long: "Monday" },
  { value: 1, label: "Tue", long: "Tuesday" },
  { value: 2, label: "Wed", long: "Wednesday" },
  { value: 3, label: "Thu", long: "Thursday" },
  { value: 4, label: "Fri", long: "Friday" },
  { value: 5, label: "Sat", long: "Saturday" },
  { value: 6, label: "Sun", long: "Sunday" },
];

// The three day-of-week QUICK-PICKS the "When" section offers — each simply
// SETS days_of_week to one of these three fixed sets. Any other combination
// the operator toggles by hand is a plain "weekly" rule (see
// deriveRepeatType below).
export const EVERY_DAY = [0, 1, 2, 3, 4, 5, 6];
export const WEEKDAYS = [0, 1, 2, 3, 4];
export const WEEKEND_DAYS = [5, 6];

// repeat_type is COSMETIC on the backend (matching is done purely on
// days_of_week + time window + effective date range — see
// PricingRuleCreate.validate_schedule); it is still a required, validated,
// stored enum field the rule list / summary reads for a human-friendly
// cadence word. The redesigned "When" section has NO repeat_type control —
// the day chips + quick-picks ARE the control — so repeat_type is DERIVED
// from the chosen day set at submit time, mirroring the exact set-matching
// summarize-rule.ts already does for its (separate, string) day summary.
// Every rule authored here has >=1 day selected (the schema refine below), so
// this never needs to (and never does) produce "never".
export function deriveRepeatType(days: number[]): RepeatType {
  const sorted = [...new Set(days)].sort((a, b) => a - b);
  const key = sorted.join(",");
  if (key === EVERY_DAY.join(",")) return "daily";
  if (key === WEEKDAYS.join(",")) return "weekdays";
  if (key === WEEKEND_DAYS.join(",")) return "weekends";
  return "weekly";
}

// Presentation-layer schema — PRESENCE / SHAPE validation (form UX) only, not
// business logic. The backend (PricingRuleCreate.validate_schedule) is the
// single authority on valid pricing: it owns price computation, eligibility,
// precedence, and the cross-field schedule rules (e.g. end_time > start_time).
// The FE only blocks submit on the fields the operator must supply so they
// aren't bounced by an uninterpretable 422.
export const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;
export const MONEY = /^\d+(\.\d{1,2})?$/;

export const schema = z
  .object({
    // Optional operator-supplied display label. Presentation only — the list
    // prefers it over the generated summary; never used for pricing.
    name: z.string().trim(),
    service: z.enum(PRICING_SERVICES as [PricingService, ...PricingService[]]),
    // Slice 6.1: the "offering" a service="classes" rule prices — a track_id.
    // Empty ("") for every other service; required (and the ONLY targeting
    // dimension) when service === "classes" — see the superRefine below and
    // the backend's validate_classes_rule (a classes rule must never also
    // carry court_ids/court_tags).
    offering_id: z.string().trim(),
    base_price: z
      .string()
      .trim()
      .min(1, "Base price is required")
      .regex(MONEY, "Enter a valid amount"),
    charge_unit: z.enum(CHARGE_UNITS as [ChargeUnit, ...ChargeUnit[]]),
    membership_scope: z.enum(
      MEMBERSHIP_SCOPES as [MembershipScope, ...MembershipScope[]],
    ),
    repeat_type: z.enum(REPEAT_TYPES as [RepeatType, ...RepeatType[]]),
    all_day: z.boolean(),
    start_time: z.string().trim(),
    end_time: z.string().trim(),
    start_date: z.string().trim(),
    end_date: z.string().trim(),
    court_ids: z.array(z.string()),
    court_tags: z.string().trim(),
    membership_plan_ids: z.array(z.string()),
    days_of_week: z.array(z.number()),
    included_player_count: z.string().trim(),
    extra_player_fee: z.string().trim(),
    entry_fee: z.string().trim(),
  })
  .superRefine((val, ctx) => {
    // Times are required only for a windowed (non all-day) rule — the backend
    // fills 00:00–23:59 for an all-day rule, so forcing times here would make a
    // valid backend feature unreachable.
    if (!val.all_day) {
      // The two time fields use DISTINCT format-error copy so a test (and an
      // operator) can pinpoint which field is wrong — only the start field's
      // hint carries the literal "HH:MM" pattern, the end field phrases it
      // differently, so the page never shows two identical error strings.
      if (!val.start_time) {
        ctx.addIssue({
          code: "custom",
          message: "Enter a start time",
          path: ["start_time"],
        });
      } else if (!HHMM.test(val.start_time)) {
        ctx.addIssue({
          code: "custom",
          message: "Use HH:MM",
          path: ["start_time"],
        });
      }
      if (!val.end_time) {
        ctx.addIssue({
          code: "custom",
          message: "Enter an end time",
          path: ["end_time"],
        });
      } else if (!HHMM.test(val.end_time)) {
        ctx.addIssue({
          code: "custom",
          message: "Enter a valid 24-hour time",
          path: ["end_time"],
        });
      }
    }
    // The "When" day chips always require at least one day — a rule with zero
    // days is meaningless (there's no "weekly-only" gate anymore; days are
    // always explicit, see deriveRepeatType / the rule-editor submit builder).
    if (val.days_of_week.length === 0) {
      ctx.addIssue({
        code: "custom",
        message: "Select at least one day",
        path: ["days_of_week"],
      });
    }
    // specific_plans must name at least one plan. The message intentionally
    // avoids the word "plan" / "select at least one" so it never double-matches
    // the Member-scope trigger (which displays "Specific plans" once chosen).
    if (
      val.membership_scope === "specific_plans" &&
      val.membership_plan_ids.length === 0
    ) {
      ctx.addIssue({
        code: "custom",
        message: "Choose at least one membership",
        path: ["membership_plan_ids"],
      });
    }
    // A classes rule MUST target exactly one offering (track) — mirrors the
    // backend's validate_classes_rule, which rejects a classes rule with no
    // offering_id (or one that also carries court_ids/court_tags — the
    // editor hides those fields for this service, see rule-editor.tsx).
    if (val.service === "classes" && !val.offering_id) {
      ctx.addIssue({
        code: "custom",
        message: "Choose the class this rate applies to",
        path: ["offering_id"],
      });
    }
    // Optional money fields, when supplied, share the base_price shape gate.
    if (val.extra_player_fee && !MONEY.test(val.extra_player_fee)) {
      ctx.addIssue({
        code: "custom",
        message: "Enter a valid amount",
        path: ["extra_player_fee"],
      });
    }
    if (val.entry_fee && !MONEY.test(val.entry_fee)) {
      ctx.addIssue({
        code: "custom",
        message: "Enter a valid amount",
        path: ["entry_fee"],
      });
    }
    // Presence-level cross-field date check (the backend owns the authoritative
    // schedule rules; this only blocks the obvious end-before-start typo).
    if (val.start_date && val.end_date && val.end_date < val.start_date) {
      ctx.addIssue({
        code: "custom",
        message: "The end date must be on or after the start date",
        path: ["end_date"],
      });
    }
  });

export type FormValues = z.infer<typeof schema>;

export const EMPTY_FORM: FormValues = {
  name: "",
  service: "court_reservation",
  offering_id: "",
  base_price: "",
  charge_unit: "per_hour",
  membership_scope: "any",
  repeat_type: "daily",
  all_day: false,
  start_time: "",
  end_time: "",
  start_date: "",
  end_date: "",
  court_ids: [],
  court_tags: "",
  membership_plan_ids: [],
  // A brand-new rule defaults to "Every day" — the common case needs zero
  // clicks. `repeat_type` above is kept in sync with this default (both are
  // overwritten together at submit time by deriveRepeatType; see rule-editor
  // .tsx), but a duplicated literal here would drift, so EVERY_DAY is the
  // single source of truth for both.
  days_of_week: EVERY_DAY,
  included_player_count: "",
  extra_player_fee: "",
  entry_fee: "",
};

export type CourtOption = { id: string; name: string };
export type PlanOption = { id: string; name: string };
export interface ClassOfferingPickerProps {
  facilityId: string;
  authorityGeneration: string;
  offeringId: string;
  disabled: boolean;
  onChange: (offeringId: string) => void;
}
